// src/pages/DataPrivacyPage.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { showToast } from '../components/ui/toast';
import {
  Shield,
  Lock,
  Cloud,
  Smartphone,
  CheckCircle,
  AlertCircle,
  Download,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  HardDrive,
  Settings,
  Info,
  BookOpen,
  BarChart3
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const DataPrivacyPage = () => {
  const {
    getPreferences,
    updatePreferences,
    exportUserData,
    resetProgress
  } = useApp();

  const [preferences, setPreferences] = useState({
    cloudSync: false,
    analytics: false,
    progressTracking: true,
    dataRetentionPeriod: 'indefinite',
    encryptionLevel: 'maximum'
  });

  const [activeTab, setActiveTab] = useState('overview');
  const [storageStats, setStorageStats] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const isSignedIn = !!user;

  useEffect(() => {
    loadPreferences();
    loadStorageStats();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await getPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const loadStorageStats = async () => {
    try {
      // Real numbers measured from this browser's localStorage — never invent stats here.
      let bytes = 0;
      let itemCount = 0;
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key) || '';
        bytes += key.length + value.length;
        itemCount += 1;
      }
      const totalSize = bytes >= 1024 * 1024
        ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(bytes / 1024))} KB`;
      setStorageStats({ totalSize, itemCount });
    } catch (error) {
      console.error('Error loading storage stats:', error);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    if (key === 'cloudSync' && !isSignedIn && value === true) {
      showToast('Please sign in to enable cloud sync', { tone: 'warn' });
      return;
    }

    try {
      const newPreferences = { ...preferences, [key]: value };
      setPreferences(newPreferences);
      await updatePreferences(newPreferences);

      setOperationStatus({
        type: 'success',
        message: 'Settings updated successfully.'
      });

      setTimeout(() => setOperationStatus(null), 3000);
    } catch (error) {
      setOperationStatus({
        type: 'error',
        message: 'Failed to update settings.'
      });
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const data = await exportUserData();

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `profound-philosophy-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setOperationStatus({
        type: 'success',
        message: 'Data exported successfully.'
      });

      setTimeout(() => setOperationStatus(null), 3000);
    } catch (error) {
      setOperationStatus({
        type: 'error',
        message: 'Export failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllData = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete ALL your data? This cannot be undone.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await resetProgress(true);

      setOperationStatus({
        type: 'success',
        message: 'All data cleared successfully.'
      });

      await loadStorageStats();
    } catch (error) {
      setOperationStatus({
        type: 'error',
        message: 'Failed to clear data.'
      });
    } finally {
      setLoading(false);
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-amber-400" />
          Your Data Storage
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Storage Location:</span>
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-green-400" />
                <span className="text-green-400">Your Device</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Storage format:</span>
              <span className="text-white">Plain JSON in this browser</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Data Size:</span>
              <span className="text-white">{storageStats?.totalSize || 'Calculating...'}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Items Stored:</span>
              <span className="text-white">{storageStats?.itemCount || 0}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Cloud sync:</span>
              <div className="flex items-center gap-2">
                {isSignedIn ? (
                  <>
                    <Cloud className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400">On while signed in</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400">Local only (signed out)</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Access:</span>
              <span className="text-green-400">Private</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Sharing:</span>
              <span className="text-green-400">None</span>
            </div>
          </div>
        </div>

      </Card>

      <Card className="p-6 bg-gradient-to-r from-green-900/20 to-blue-900/20 border-green-500/30">
        <div className="flex items-start gap-4">
          <Shield className="w-8 h-8 text-green-400 mt-1" />
          <div>
            <h2 className="text-lg font-semibold text-white mb-2">Your Privacy, Honestly</h2>
            <div className="space-y-2 text-slate-300">
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Signed out, everything stays in this browser; signed in, your data syncs only to your own private space, protected by per-user security rules
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Nothing is sold or shared with advertisers; there is no ad tech in this app
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                You own your data and can export or delete it anytime
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const SettingsTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <Cloud className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-white">Cloud Sync</h2>
        </div>
        <p className="text-slate-400 text-sm">
          Sync follows your sign-in. While you're signed in, your progress saves to your own
          private cloud space automatically; sign out and everything stays on this device only.
          There are no separate switches to manage.
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-semibold text-white">Google Analytics</h2>
            </div>
            <p className="text-slate-400 text-sm">
              We count visits in totals using Cloudflare Web Analytics (no cookies, and it
              doesn't identify you). If you say yes when we ask, Google Analytics also counts
              which pages and features get used. It keeps a random ID in a cookie on this
              device and never sees what you write, and its advertising features are switched
              off. Say no, or turn on your browser's Global Privacy Control, and it never
              loads. You can change your choice any time with this switch. No third-party ads,
              and we never sell your data.
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={preferences.analytics === true}
            aria-label="Google Analytics"
            onClick={() => handlePreferenceChange('analytics', !preferences.analytics)}
            className={`relative mt-1 h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
              preferences.analytics ? 'bg-emerald-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                preferences.analytics ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-red-400" />
          How Your Data Is Stored
        </h2>

        <div className="p-4 bg-white/5 rounded-lg">
          <p className="text-slate-400 text-sm">
            On this device, your data is kept as plain JSON in the browser's local storage,
            private to this browser profile, but readable by anyone who can unlock this device.
            When you sign in, progress syncs over an encrypted connection (TLS) to your own
            space in Google Firestore, which encrypts data at rest. The app adds no extra
            encryption layer of its own.
          </p>
        </div>
      </Card>
    </div>
  );

  const ManagementTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-400" />
          Export Your Data
        </h2>

        <p className="text-slate-300 mb-4">
          Download a complete backup of all your Profound Philosophy data in JSON format.
          This includes your progress, reflections, and more.
        </p>

        <Button
          onClick={handleExportData}
          disabled={loading}
          className="w-full sm:w-auto"
        >
          <Download className="w-4 h-4 mr-2" />
          {loading ? 'Exporting...' : 'Export All Data'}
        </Button>
      </Card>


      <Card className="p-6 bg-red-900/20 border-red-500/30">
        <h2 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          Clear All Data
        </h2>

        <p className="text-slate-300 mb-4">
          Permanently delete all your data from this device and cloud storage.
          <strong className="text-red-300"> This cannot be undone.</strong>
        </p>

        <Button
          onClick={handleClearAllData}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {loading ? 'Clearing...' : 'Clear All Data'}
        </Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-32">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Data & Privacy
            </h1>
            <p className="text-lg text-slate-300">
              Your philosophy journey is private, secure, and always under your control.
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="bg-slate-800/50 rounded-lg p-1 flex flex-wrap justify-center gap-1">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'management', label: 'Data Management', icon: HardDrive }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  aria-pressed={activeTab === tab.id}
                  className={`px-4 py-2 rounded-md font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-amber-700 text-white'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {OverviewTab()}
              </motion.div>
            )}

            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {SettingsTab()}
              </motion.div>
            )}

            {activeTab === 'management' && (
              <motion.div
                key="management"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {ManagementTab()}
              </motion.div>
            )}
          </AnimatePresence>

          {operationStatus && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              role={operationStatus.type === 'error' ? 'alert' : 'status'}
              className={`fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 p-4 rounded-lg shadow-lg max-w-sm ${
                operationStatus.type === 'success'
                  ? 'bg-green-900/90 text-green-300 border border-green-500/30'
                  : 'bg-red-900/90 text-red-300 border border-red-500/30'
              }`}
            >
              <div className="flex items-center gap-2">
                {operationStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{operationStatus.message}</span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataPrivacyPage;
